
import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, FileText, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';

interface Activity {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  user: string;
  status: 'completed' | 'pending' | 'warning' | 'error';
  category: string;
}

interface RecentActivitiesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RecentActivitiesModal = ({ isOpen, onClose }: RecentActivitiesModalProps) => {
  const activities: Activity[] = [
    {
      id: '1',
      type: 'data_entry',
      title: 'Energy consumption data updated',
      description: 'Monthly electricity usage data for December 2024 has been recorded',
      timestamp: '2024-12-15 14:30',
      user: 'John Smith',
      status: 'completed',
      category: 'Environmental'
    },
    {
      id: '2',
      type: 'assessment',
      title: 'ESG assessment completed',
      description: 'Quarterly ESG assessment for Q4 2024 has been finalized',
      timestamp: '2024-12-14 16:45',
      user: 'Sarah Johnson',
      status: 'completed',
      category: 'Governance'
    },
    {
      id: '3',
      type: 'report',
      title: 'Sustainability report generated',
      description: 'Annual sustainability report for 2024 has been created',
      timestamp: '2024-12-13 10:20',
      user: 'Mike Wilson',
      status: 'pending',
      category: 'Reporting'
    },
    {
      id: '4',
      type: 'data_validation',
      title: 'Data validation required',
      description: 'Carbon footprint calculations need verification',
      timestamp: '2024-12-12 09:15',
      user: 'Emily Davis',
      status: 'warning',
      category: 'Environmental'
    },
    {
      id: '5',
      type: 'target_update',
      title: 'Net-zero target updated',
      description: 'Carbon neutrality target adjusted to 2030',
      timestamp: '2024-12-11 13:50',
      user: 'David Brown',
      status: 'completed',
      category: 'Environmental'
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Environmental':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'Governance':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'Reporting':
        return <FileText className="h-4 w-4 text-purple-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            All Recent Activities
          </DialogTitle>
          <DialogDescription>
            Complete history of all system activities and user interactions
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-4">
            {activities.map((activity) => (
              <div key={activity.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(activity.status)}
                      {getCategoryIcon(activity.category)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium">{activity.title}</h3>
                        <Badge className={getStatusColor(activity.status)}>
                          {activity.status}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">
                        {activity.description}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {activity.timestamp}
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {activity.user}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {activity.category}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-gray-500">
            Showing {activities.length} activities
          </div>
          <Button onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
